import { AUTHOR_TYPES } from './conversationConstants';

const normalizeUpperCase = (value) =>
  typeof value === 'string' ? value.toUpperCase() : value;

const getMessageTimestamp = (message = {}) =>
  message.createdAt || 0;

export const transformMessage = (apiMessage) => {
  const {
    messageId,
    authorType,
    status,
    createdAt,
    msgType,
    content = {},
    waMessageId,
    direction,
    channel,
    conversationId,
  } = apiMessage;

  if (!messageId && !waMessageId && !createdAt) {
    throw new Error('Message ID or timestamp is required');
  }

  const resolvedAuthorType = normalizeUpperCase(authorType);
  const resolvedStatus = normalizeUpperCase(status);
  const resolvedMsgType = normalizeUpperCase(
    msgType || content.type || 'TEXT'
  );
  const resolvedMessageId =
    messageId || waMessageId || `${createdAt || Date.now()}`;
  const resolvedDirection = direction || (resolvedAuthorType === 'AGENT' ? 'OUTBOUND' : 'INBOUND');

  // Determine sender type for UI (legacy format)
  const senderMap = {
    [AUTHOR_TYPES.CUSTOMER]: 'customer',
    [AUTHOR_TYPES.AGENT]: 'admin',
  };

  // Map API message status to UI message status (legacy format)

  // Convert timestamp (milliseconds) to readable time
  const messageTime = createdAt
    ? formatMessageTime(createdAt)
    : 'Unknown time';

  // Extract text content based on message type (for legacy format)
  let messageText = '';
  switch (resolvedMsgType) {
    case 'TEXT':
      messageText = content.text || '';
      break;
    case 'IMAGE':
      messageText = content.caption || '[Image]';
      break;
    case 'DOCUMENT':
      messageText = `[Document: ${content.filename || 'Unknown'}]`;
      break;
    case 'INTERACTIVE':
      messageText = content.interactive?.title || '[Interactive Message]';
      break;
    case 'BUTTON':
      messageText = content.button?.text || '[Button Message]';
      break;
    case 'AUDIO':
      messageText = '[Audio Message]';
      break;
    case 'VIDEO':
      messageText = '[Video Message]';
      break;
    case 'TEMPLATE':
      messageText = '[Template Message]';
      break;
    default:
      messageText = '[Message]';
  }

  // Return new API format with backward-compatible legacy fields
  return {
    // New API format (required for new MessageList component)
    messageId: resolvedMessageId,
    conversationId,
    content: {
      type: resolvedMsgType,
      ...content, // Preserve all original content fields (text, media, template, etc.)
      text: content.text || (resolvedMsgType === 'BUTTON' ? content.button?.text : resolvedMsgType === 'INTERACTIVE' ? content.interactive?.title : ''),
    },
    authorType: resolvedAuthorType,
    direction: resolvedDirection,
    status: resolvedStatus,
    channel,
    createdAt,

    // Legacy format fields (for backward compatibility)
    id: resolvedMessageId,
    text: messageText,
    sender: senderMap[resolvedAuthorType] || 'customer',
    time: messageTime,
    messageType: resolvedMsgType,
  };
};

export const transformConversation = (apiConversation, apiMessages = []) => {
  // Extract only fields that exist in the API response
  // No assumptions or fallbacks for missing fields
  const {
    conversationId,
    customerId,
    customerName,
    customerAddress,
    status,
    lastMessagePreview,
    lastMessageAt,
    agentUnreadCount,
    unreadCount,
    conversationType,
    assignedAgentId,
    channel,
    channelId,
  } = apiConversation;

  if (!conversationId) {
    throw new Error('Conversation ID is required');
  }

  const transformedMessages = [...apiMessages]
    .sort((a, b) => Number(getMessageTimestamp(a)) - Number(getMessageTimestamp(b)))
    .map(transformMessage);

  const messageTime = lastMessageAt
    ? formatMessageTime(lastMessageAt)
    : 'Unknown';

  return {
    // Core IDs
    id: conversationId,
    conversationId,
    customerId,
    
    // Customer Info (use customerAddress as phone/identifier if available)
    name: customerName || customerAddress || customerId,
    customerName,
    phone: customerAddress || '',
    
    // Message Info
    lastMessage: lastMessagePreview || '',
    time: messageTime,
    
    // Unread counts (use agentUnreadCount if available, fallback to unreadCount)
    unread: Number(agentUnreadCount !== undefined ? agentUnreadCount : unreadCount) || 0,
    
    // Messages array
    messages: transformedMessages,
    
    // Metadata
    status,
    conversationType,
    assignedAgentId,
    channel,
    channelId,
    lastMessageAt,
    mode: apiConversation.mode,
  };
};

export const formatMessageTime = (timestamp) => {
  if (!timestamp) return 'Unknown';

  const milliseconds = timestamp > 9999999999 ? timestamp : timestamp * 1000;
  const date = new Date(milliseconds);

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (messageDate.getTime() === today.getTime()) {
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  }

  if (messageDate.getTime() === yesterday.getTime()) {
    return 'Yesterday';
  }

  return date.toLocaleDateString('en-IN', {
    month: 'short',
    day: 'numeric',
  });
};

export const transformApiResponse = (apiResponse) => {
  const { conversation, messages = [] } = apiResponse;

  if (!conversation) {
    throw new Error('Invalid API response: missing conversation object');
  }

  return {
    conversation: transformConversation(conversation, messages),
    nextKey: apiResponse.nextKey || null,
  };
};

export const transformConversationsListResponse = (apiResponse) => {
  const conversations = Array.isArray(apiResponse?.conversations)
    ? apiResponse.conversations
    : [];

  return {
    conversations: conversations.map((conversation) =>
      transformConversation(conversation, [])
    ),
    nextKey: apiResponse?.nextKey || null,
  };
};
